import { ArrowLeft, X } from "lucide-react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { useState } from "react";
import type { ReactNode } from "react";
import bgMission from "@/assets/bg-mission-2.png";
import { WorkshopNavigator } from "@/components/shell/WorkshopNavigator";
import { pageTransitionReduced, panelCrossfade } from "@/lib/motion";
import { useNavigation } from "@/stores/useNavigation";
import { CapabilityRing } from "./CapabilityRing";
import { OpeningScreen } from "./OpeningScreen";
import { ProgramRollupPanel } from "./ProgramRollupPanel";
import { WorkshopPanel } from "./WorkshopPanel";
import { Ws8Panel } from "./Ws8Panel";
import type { WorkshopPanelData } from "./workshop-panel-data";

/**
 * Phase 1 (5/N) — all eight workshop panels port across via the
 * data-driven WorkshopPanel component, completing the `mc.html` IA.
 *
 * Layout:
 *   - `OPENING`              full-screen XAES wordmark + plasma orb
 *   - `MC_00`                Mission Control capability ring
 *   - `MC_WS1` … `MC_WS8`    workshop bubble panels (driven by
 *                            workshop-panel-data.ts)
 *
 * WorkshopNavigator sticks to the bottom whenever we're past the
 * opening screen. Keyboard shortcuts: 1-8 selects a workshop, Escape
 * returns to MC_00.
 *
 * Panel / opening swaps use a stacked opacity crossfade (not `mode="wait"`)
 * so the viewport never empties between workshops, Back, or Close.
 */

const PANEL_TO_WORKSHOP: Record<string, WorkshopPanelData["workshopId"]> = {
  MC_WS1: "WS1",
  MC_WS2: "WS2",
  MC_WS3: "WS3",
  MC_WS4: "WS4",
  MC_WS5: "WS5",
  MC_WS6: "WS6",
  MC_WS7: "WS7",
  MC_WS8: "WS8",
};

function panelBody(activePanel: string): ReactNode {
  if (activePanel === "OPENING") return null;
  if (activePanel === "MC_00") return <CapabilityRing />;
  if (activePanel === "MC_WS8") return <Ws8Panel />;
  const workshopId = PANEL_TO_WORKSHOP[activePanel];
  return workshopId ? <WorkshopPanel workshopId={workshopId} /> : null;
}

function ActivePanelContent() {
  const activePanel = useNavigation((s) => s.activePanel);
  const reduceMotion = useReducedMotion();
  const variants = reduceMotion ? pageTransitionReduced : panelCrossfade;
  const body = panelBody(activePanel);
  if (!body) return null;

  return (
    <AnimatePresence initial={false}>
      <motion.div
        key={activePanel}
        className="absolute inset-0 overflow-hidden"
        initial={variants.initial}
        animate={variants.animate}
        exit={variants.exit}
      >
        {body}
      </motion.div>
    </AnimatePresence>
  );
}

export function MissionControl() {
  const activePanel = useNavigation((s) => s.activePanel);
  const setActivePanel = useNavigation((s) => s.setActivePanel);
  const reduceMotion = useReducedMotion();
  const variants = reduceMotion ? pageTransitionReduced : panelCrossfade;
  const isOpening = activePanel === "OPENING";
  const isMc00 = activePanel === "MC_00";
  // Phone-tier MC_00 shows the ring OR the rollup, not a squeezed slice of
  // both — stacking fixed-height cells crushed the ring's bubbles down to
  // ~90px and still clipped the rollup mid-heading. Desktop keeps the
  // original side-by-side two-column layout unconditionally (`lg:` below
  // always wins); this toggle only matters below `lg`.
  const [mobilePane, setMobilePane] = useState<"ring" | "rollup">("ring");

  return (
    <div className="relative isolate flex h-full flex-col overflow-hidden">
      {/* Background photograph, full opacity, matching the legacy
          `_legacy/mc.html` opening view where the photo sat prominently
          behind the OPENING / MC_00 panels — no wash on top of it. */}
      <div
        aria-hidden
        className="absolute inset-0 -z-10 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${bgMission})` }}
      />

      <div className="relative min-h-0 flex-1">
        <AnimatePresence initial={false}>
          {isOpening ? (
            <motion.div
              key="opening"
              className="absolute inset-0"
              initial={variants.initial}
              animate={variants.animate}
              exit={variants.exit}
            >
              <OpeningScreen />
            </motion.div>
          ) : (
            /* Legacy `_legacy/mc.html` MC_00 has padding `20px 140px 140px
               140px` — the panel is inset from the viewport edges (140 px
               sides, 20 px top, 140 px bottom reserves the bottom-nav
               strip). React layout uses flex with the bottom-nav as a
               sibling, so the bottom reservation is handled by flex; we
               only need to match the top + side insets. Bottom padding
               drops to `pb-2` below `lg` — the Workshop Dashboard route
               only uses `p-2` around its own console (WorkshopDashboard.tsx),
               so its content sits right up against the nav; MC_00's full
               `pb-5` (20px) left a gap of bare office-photo background
               between the console and the nav bar that WD's tighter
               padding doesn't have. */
            <motion.section
              key="console"
              className="absolute inset-0 mx-auto flex w-full min-h-0 max-w-[2200px] flex-col px-[clamp(12px,8vw,140px)] pt-5 pb-2 lg:pb-5"
              initial={variants.initial}
              animate={variants.animate}
              exit={variants.exit}
            >
              {/* Legacy `_legacy/mc.html` MC_00 (lines 1458-1657) wraps the
                  entire mission control body in a `.mc-panel-glow` shell with
                  a bar-top header (Mission Control title + close action), an
                  eyebrow row, the two-column grid, and a bar-btm footer
                  (DXC.TECHNOLOGY + Agent Liaison). We re-author the same
                  chrome here using the `.panel-glow-mc` class that already
                  exists in `src/index.css`. */}
              <div className="panel-glow-mc bg-background relative flex min-h-0 flex-1 flex-col overflow-hidden">
                {/* bar-top — panel header.
                    MC_00: legacy `✕ CLOSE` → opening screen (`XAES.toDefault()`).
                    MC_WSn preview: legacy `← BACK` → MC_00 (capability ring +
                    program rollup). Escape does the same via WorkshopNavigator. */}
                <div
                  className="flex flex-shrink-0 items-center px-4 py-2.5"
                  style={{
                    borderBottom: "1px solid rgba(var(--accent-true-blue-rgb), 0.25)",
                    background: "var(--bg-panel)",
                  }}
                >
                  <div className="flex items-center gap-2.5">
                    <span
                      aria-hidden
                      className="inline-block h-2 w-2 flex-shrink-0 animate-pulse rounded-full"
                      style={{
                        background: "var(--accent-true-blue)",
                        boxShadow: "var(--glow-blue)",
                      }}
                    />
                    <span
                      className="font-sans font-bold uppercase"
                      style={{
                        fontSize: "var(--fs-heading)",
                        letterSpacing: "4px",
                        color: "var(--text-primary)",
                      }}
                    >
                      Mission Control
                    </span>
                  </div>
                  {isMc00 ? (
                    <button
                      type="button"
                      onClick={() => setActivePanel("OPENING")}
                      aria-label="Close Mission Control"
                      className="modal-close-btn ml-auto inline-flex items-center gap-1.5"
                      style={{ fontSize: "var(--fs-label)", letterSpacing: "1.5px" }}
                    >
                      <X aria-hidden style={{ width: 11, height: 11 }} strokeWidth={2.5} />
                      Close
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setActivePanel("MC_00")}
                      aria-label="Back to program rollup"
                      className="modal-close-btn ml-auto inline-flex items-center gap-1.5"
                      style={{ fontSize: "var(--fs-label)", letterSpacing: "1.5px" }}
                    >
                      <ArrowLeft aria-hidden style={{ width: 11, height: 11 }} strokeWidth={2.5} />
                      Back
                    </button>
                  )}
                </div>

                {/* eyebrow row */}
                <div className="flex-shrink-0 px-4 pt-3 pb-2">
                  <div className="flex items-center gap-2">
                    <span
                      aria-hidden
                      className="inline-block h-2 w-2 flex-shrink-0 animate-pulse rounded-full"
                      style={{
                        background: "var(--accent-green)",
                        boxShadow: "var(--glow-green)",
                      }}
                    />
                    <span
                      className="font-sans font-bold uppercase"
                      style={{
                        color: "var(--accent-true-blue)",
                        fontSize: "var(--fs-sub)",
                        letterSpacing: "1.5px",
                      }}
                    >
                      Mission Control
                    </span>
                  </div>
                </div>

                {/* Phone-tier switch — only meaningful below `lg` (see
                    `mobilePane` above). Both panes stay mounted; this just
                    toggles which one is visible so neither loses state
                    (ring's ResizeObserver, rollup's scroll position) when
                    the user flips back. */}
                {isMc00 && (
                  <div
                    className="mc-mobile-tabs flex flex-shrink-0 gap-2 px-4 pb-2 lg:hidden"
                    role="tablist"
                    aria-label="Mission Control view"
                  >
                    <button
                      type="button"
                      role="tab"
                      aria-selected={mobilePane === "ring"}
                      onClick={() => setMobilePane("ring")}
                      className={`mc-mobile-tab ${mobilePane === "ring" ? "mc-mobile-tab-active" : ""}`}
                    >
                      XAES
                    </button>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={mobilePane === "rollup"}
                      onClick={() => setMobilePane("rollup")}
                      className={`mc-mobile-tab ${mobilePane === "rollup" ? "mc-mobile-tab-active" : ""}`}
                    >
                      Program Rollup
                    </button>
                  </div>
                )}

                {/* Two-column body — legacy `_legacy/mc.html` line 1481
                    `grid-template-columns:1fr 340px`. Left holds the active
                    panel (capability ring / workshop drill-down). Right is
                    the program rollup on MC_00 only. Below `lg`, only one
                    of the two is visible at a time (`mobilePane`, toggled by
                    the tabs above) so the visible pane gets the *entire*
                    available height instead of splitting a fixed-height
                    budget with the other — that split crushed the ring to
                    ~90px bubbles and still clipped the rollup mid-heading. */}
                <div
                  className={`grid min-h-0 flex-1 gap-3 px-4 pb-3 ${
                    isMc00 ? "lg:grid-cols-[1fr_340px]" : "lg:grid-cols-1"
                  }`}
                >
                  <div
                    className={`border-true-blue/40 bg-secondary/40 relative min-h-0 overflow-hidden rounded-xl border shadow-[0_0_60px_rgba(var(--accent-true-blue-rgb),0.10)] ${
                      isMc00 && mobilePane !== "ring" ? "hidden lg:block" : ""
                    }`}
                  >
                    <ActivePanelContent />
                  </div>
                  {isMc00 && (
                    <aside
                      className={`min-h-0 flex-col items-stretch overflow-hidden rounded-[10px] lg:flex ${
                        mobilePane === "rollup" ? "flex" : "hidden"
                      }`}
                      style={{
                        background: "var(--bg-panel-alt)",
                        border: "1px solid rgba(var(--accent-true-blue-rgb), 0.15)",
                        padding: "10px 12px",
                      }}
                    >
                      <ProgramRollupPanel />
                    </aside>
                  )}
                </div>

                {/* bar-btm — panel footer */}
                <div
                  className="flex flex-shrink-0 items-center gap-2 px-4 py-1.5"
                  style={{
                    borderTop: "1px solid rgba(var(--accent-true-blue-rgb), 0.25)",
                    background: "var(--bg-panel-alt)",
                  }}
                >
                  <span
                    className="font-mono uppercase"
                    style={{
                      color: "var(--text-muted)",
                      fontSize: "var(--fs-label)",
                      letterSpacing: "1px",
                    }}
                  >
                    DXC.TECHNOLOGY
                  </span>
                  <span
                    className="ml-auto flex items-center gap-1.5 uppercase"
                    style={{
                      color: "var(--accent-true-blue)",
                      fontSize: "var(--fs-label)",
                      letterSpacing: "1px",
                    }}
                  >
                    <span
                      aria-hidden
                      className="inline-block h-1.5 w-1.5 flex-shrink-0 animate-pulse rounded-full"
                      style={{
                        background: "var(--accent-green)",
                        boxShadow: "var(--glow-green)",
                      }}
                    />
                    Agent Liaison: XAES Analyst
                  </span>
                </div>
              </div>
            </motion.section>
          )}
        </AnimatePresence>
      </div>

      <WorkshopNavigator />
    </div>
  );
}
